import { Injectable } from "@angular/core";
import {
  PRIMITIVE,
  ServiceMessageBrokerFactory,
  ServiceMessageBroker,
} from "shared/provider-webworker";

import * as CubiqTS from "shared/cubiq-model-multi-asset";

import { BaseLookupService } from "../../impl/BaseLookupService";
import { BranchLookupService } from "./BranchLookupService";
import { BranchPromiseLookupService } from "./BranchPromiseLookupService";
import { ClientTraderModelPrivatization } from "../../../model/privatization/ClientTraderModelPrivatization";
import { CubiqWorker } from "../../../worker/CubiqWorker";
import { IUserPromiseLookupService } from "../IUserPromiseLookupService";
import { PrimaryLookupService } from "../../impl/PrimaryLookupService";
import { PromiseLookupService } from "../../impl/PromiseLookupService";
import { UserLookupService } from "./UserLookupService";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

@Injectable()
export class UserPrimaryLookupService
  extends PrimaryLookupService<string, CubiqTS.UserImpl>
  implements IUserPromiseLookupService
{
  public changePasswordRequested: boolean = false;

  constructor(
    protected base: UserLookupService,
    protected factory: ServiceMessageBrokerFactory,
    protected cubiqWorker: CubiqWorker,
    protected branchLookup: BranchPromiseLookupService,
    protected clientTraderModelPrivatization: ClientTraderModelPrivatization,
  ) {
    super(base, factory, CubiqTS.UserImpl, cubiqWorker, "UserImpl");

    cubiqWorker.isAuth(`${this.constructor.name}`).then(
      () => {
        let usersObservable = cubiqWorker
          .primeStaticData<CubiqTS.UserImpl>(CubiqTS.UserImpl.name)
          .subscribe(
            (Users: CubiqTS.UserImpl[]) => {
              this.base.prime(Users);
            },
            (error: Error) => {
              console.debug("User prime failed");
              this._primed.reject(error);
            },
            () => {
              this.clientTraderModelPrivatization.ready.then(() => {
                this.addPrivatization();
                this._primed.resolve(true);
              });
            },
          );
      },
      (reason) => {
        console.debug("UserPrimaryLookupService: Failed to prime: " + reason);

        this._primed.resolve(false);
      },
    );

    this.broker.registerMethod("getUser", [PRIMITIVE], this.getUser.bind(this), this.VALUE);
    this.broker.registerMethod("getUsers", [PRIMITIVE], this.getUsers.bind(this), this.VALUE);
    this.broker.registerMethod(
      "getUsersByClientCode",
      [PRIMITIVE],
      this.getUsersByClientCode.bind(this),
      this.VALUE,
    );
    this.broker.registerMethod("getLoggedInUser", [], this.getLoggedInUser.bind(this), this.VALUE);
    this.broker.registerMethod(
      "changePassword",
      [PRIMITIVE, PRIMITIVE, PRIMITIVE, PRIMITIVE, PRIMITIVE],
      this.changePassword.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "isChangePasswordRequested",
      [],
      this.isChangePasswordRequested.bind(this),
      PRIMITIVE,
    );
  }

  private addPrivatization(): void {
    for (let kv of this.clientTraderModelPrivatization.getPrivate().entries()) {
      // logger.out.warn(`Adding a privatized dummy client ${kv[0]} ${kv[1]}`);
      this.base.mapPrivatizationItem(kv[1], kv[0]);
    }

    this.branchLookup.isPrimed().then((primed) => {
      let baseBranchLookup = this.branchLookup.getBase() as BranchLookupService;

      if (!primed) {
        console.debug(`ClientPrimaryLookupService: failed to access branches for privatization`);
        return;
      }

      for (let branchCd of baseBranchLookup.getVisiblePrivateBranchCodes()) {
        for (let kv of this.clientTraderModelPrivatization.getBranchPrivate(branchCd).entries()) {
          // logger.out.warn(`Adding a privatized branch ${branch.branchId} dummy account ${kv[0]} ${kv[1]}`);
          this.base.mapPrivatizationItem(kv[1], kv[0]);
        }
      }
    });

    for (let kv of this.clientTraderModelPrivatization.getRestricted().entries()) {
      // logger.out.warn(`Adding a restricted dummy client ${kv[0]} ${kv[1]}`);
      this.base.mapPrivatizationItem(kv[1], kv[0]);
    }

    this.clientTraderModelPrivatization.restricted$.subscribe((restrictedClientSegment) => {
      this.base.mapPrivatizationItem(restrictedClientSegment.value, restrictedClientSegment.key);
    });
  }

  getUser(code: string): Promise<CubiqTS.UserImpl> {
    return new Promise<CubiqTS.UserImpl>((res, rej) => {
      res(this.base.getUser(code));
    });
  }

  getUsers(ids: string[]): Promise<CubiqTS.UserImpl[]> {
    return new Promise<CubiqTS.UserImpl[]>((res, rej) => {
      let result: Array<CubiqTS.UserImpl> = [];
      ids.forEach((id) => {
        result.push(this.base.getUserById(id));
      });
      res(result);
    });
  }

  getUsersByClientCode(clientCode: string): Promise<Array<CubiqTS.UserImpl>> {
    return new Promise<CubiqTS.UserImpl[]>((res, _) => {
      res(this.base.getUsersByClientCode(clientCode));
    });
  }

  getBase(): UserLookupService {
    return this.base;
  }

  getLoggedInUser(): Promise<CubiqTS.UserImpl> {
    return new Promise<CubiqTS.UserImpl>((res, rej) => {
      this.cubiqWorker.isAuth(`${this.constructor.name}`).then(() => {
        res(this.cubiqWorker.getLoggedInUser());
      });
    });
  }

  changePassword(
    oldPassword: string,
    newPassword: string,
    newPasswordConfirmation: string,
    adminRequested: boolean,
    userCd: string,
  ): Promise<any> {
    let request: CubiqTS.PasswordChangeRequestImpl = new CubiqTS.PasswordChangeRequestImpl();
    request.oldPassword = oldPassword;
    request.newPassword = newPassword;
    request.newPasswordConfirmation = newPasswordConfirmation;
    request.userCd = userCd;
    if (adminRequested) {
      return this.cubiqWorker.requestPasswordReset(request);
    } else {
      return new Promise<CubiqTS.UserImpl>((res, rej) => {
        this.changePasswordRequested = true;
        this.cubiqWorker.requestPasswordReset(request, false).then(
          (payload) => {
            setTimeout(() => {
              this.changePasswordRequested = false;
            }, 5000); // fallback in case purge message is not received
            res(payload);
          },
          (error) => {
            this.changePasswordRequested = false;
            rej(error);
          },
        );
      });
    }
  }

  isChangePasswordRequested(): Promise<boolean> {
    return new Promise<boolean>((res) => {
      res(this.changePasswordRequested);
      this.changePasswordRequested = false;
    });
  }
}
